import { } from 'react-redux';

import { Action } from '@reduxjs/toolkit';

import * as actionTypes from '../constants/actionTypes';
import { updateObject } from '../../../../shared/utils';

import { AppAction } from '../../../../store';

import {
  NCRoamSubscriptionPlan,
  NCRoamAgencySubscription,
  NCRoamUser,
  NCRoamSubscribeResponse
} from '../../models';

export interface NCRoamSubscriptionState {
  ncRoamSubscriptionPlans: NCRoamSubscriptionPlan[] | null,
  ncRoamSubscriptionPlansError: null,
  ncRoamSubscriptionPlansLoading: boolean,

  ncRoamAgencySubscription: NCRoamAgencySubscription | null,
  ncRoamAgencySubscriptionError: null,
  ncRoamAgencySubscriptionLoading: boolean,

  ncRoamUsersError: null,
  ncRoamUsersLoading: boolean,
  ncRoamUsersResult: NCRoamUser[] | null,

  ncRoamSubscribeError: null,
  ncRoamSubscribeLoading: boolean,
  ncRoamSubscribeResult: NCRoamSubscribeResponse | null,

  ncRoamCancelSubscriptionError: null,
  ncRoamCancelSubscriptionLoading: boolean,
  ncRoamCancelSubscriptionResult: boolean | null,

  ncRoamUpgradeSubscriptionError: null,
  ncRoamUpgradeSubscriptionLoading: boolean,
  ncRoamUpgradeSubscriptionResult: boolean | null,
};

const initialState: NCRoamSubscriptionState = {
  ncRoamSubscriptionPlans: null,
  ncRoamSubscriptionPlansError: null,
  ncRoamSubscriptionPlansLoading: false,

  ncRoamAgencySubscription: null,
  ncRoamAgencySubscriptionError: null,
  ncRoamAgencySubscriptionLoading: false,

  ncRoamUsersError: null,
  ncRoamUsersLoading: false,
  ncRoamUsersResult: null,

  ncRoamSubscribeError: null,
  ncRoamSubscribeLoading: false,
  ncRoamSubscribeResult: null,

  ncRoamCancelSubscriptionError: null,
  ncRoamCancelSubscriptionLoading: false,
  ncRoamCancelSubscriptionResult: null,

  ncRoamUpgradeSubscriptionError: null,
  ncRoamUpgradeSubscriptionLoading: false,
  ncRoamUpgradeSubscriptionResult: null,
};

const getNCRoamSubscriptionPlansStart = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscriptionPlan[] | null>) => {
  return updateObject(state, {
    ncRoamSubscriptionPlans: null,
    ncRoamSubscriptionPlansError: null,
    ncRoamSubscriptionPlansLoading: true,
  });
};

const getNCRoamSubscriptionPlansSuccess = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscriptionPlan[] | null>) => {
  return updateObject(state, {
    ncRoamSubscriptionPlans: action.data,
    ncRoamSubscriptionPlansError: null,
    ncRoamSubscriptionPlansLoading: false,
  });
};

const getNCRoamSubscriptionPlansFail = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscriptionPlan[] | null>) => {
  return updateObject(state, {
    ncRoamSubscriptionPlans: null,
    ncRoamSubscriptionPlansError: action.error,
    ncRoamSubscriptionPlansLoading: false,
  });
};

const getNCRoamAgencySubscriptionStart = (state: NCRoamSubscriptionState, action: AppAction<NCRoamAgencySubscription | null>) => {
  return updateObject(state, {
    ncRoamAgencySubscription: null,
    ncRoamAgencySubscriptionError: null,
    ncRoamAgencySubscriptionLoading: true,
  });
};

const getNCRoamAgencySubscriptionSuccess = (state: NCRoamSubscriptionState, action: AppAction<NCRoamAgencySubscription | null>) => {
  return updateObject(state, {
    ncRoamAgencySubscription: action.data,
    ncRoamAgencySubscriptionError: null,
    ncRoamAgencySubscriptionLoading: false,
  });
};

const getNCRoamAgencySubscriptionFail = (state: NCRoamSubscriptionState, action: AppAction<NCRoamAgencySubscription | null>) => {
  return updateObject(state, {
    ncRoamAgencySubscription: null,
    ncRoamAgencySubscriptionError: action.error,
    ncRoamAgencySubscriptionLoading: false,
  });
};

const getNCRoamUsersStart = (state: NCRoamSubscriptionState, action: AppAction<NCRoamUser[] | null>) => {
  return updateObject(state, {
    ncRoamUsersError: null,
    ncRoamUsersLoading: true,
  });
};

const getNCRoamUsersSuccess = (state: NCRoamSubscriptionState, action: AppAction<NCRoamUser[] | null>) => {
  return updateObject(state, {
    ncRoamUsersResult: action.data,
    ncRoamUsersError: null,
    ncRoamUsersLoading: false,
  });
};

const getNCRoamUsersFail = (state: NCRoamSubscriptionState, action: AppAction<NCRoamUser[] | null>) => {
  return updateObject(state, {
    ncRoamUsersResult: action.data,
    ncRoamUsersError: action.error,
    ncRoamUsersLoading: false,
  });
};

const ncRoamSubscribeStart = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscribeResponse | null>) => {
  return updateObject(state, {
    ncRoamSubscribeError: null,
    ncRoamSubscribeLoading: true,
  });
};

const ncRoamSubscribeSuccess = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscribeResponse | null>) => {
  return updateObject(state, {
    ncRoamSubscribeResult: action.data,
    ncRoamSubscribeError: null,
    ncRoamSubscribeLoading: false,
  });
};

const ncRoamSubscribeFail = (state: NCRoamSubscriptionState, action: AppAction<NCRoamSubscribeResponse | null>) => {
  return updateObject(state, {
    ncRoamSubscribeResult: action.data,
    ncRoamSubscribeError: action.error,
    ncRoamSubscribeLoading: false,
  });
};

const ncRoamCancelSubscriptionStart = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamCancelSubscriptionError: null,
    ncRoamCancelSubscriptionLoading: true,
  });
};

const ncRoamCancelSubscriptionSuccess = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamCancelSubscriptionResult: action.data,
    ncRoamCancelSubscriptionError: null,
    ncRoamCancelSubscriptionLoading: false,
  });
};

const ncRoamCancelSubscriptionFail = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamCancelSubscriptionResult: action.data,
    ncRoamCancelSubscriptionError: action.error,
    ncRoamCancelSubscriptionLoading: false,
  });
};

const ncRoamUpgradeSubscriptionStart = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamUpgradeSubscriptionError: null,
    ncRoamUpgradeSubscriptionLoading: true,
  });
};

const ncRoamUpgradeSubscriptionSuccess = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamUpgradeSubscriptionResult: action.data,
    ncRoamUpgradeSubscriptionError: null,
    ncRoamUpgradeSubscriptionLoading: false,
  });
};

const ncRoamUpgradeSubscriptionFail = (state: NCRoamSubscriptionState, action: AppAction<boolean | null>) => {
  return updateObject(state, {
    ncRoamUpgradeSubscriptionResult: action.data,
    ncRoamUpgradeSubscriptionError: action.error,
    ncRoamUpgradeSubscriptionLoading: false,
  });
};

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.GET_NC_ROAM_SUBSCRIPTION_PLANS_START:
      return getNCRoamSubscriptionPlansStart(state, action as AppAction<NCRoamSubscriptionPlan[] | null>);
    case actionTypes.GET_NC_ROAM_SUBSCRIPTION_PLANS_SUCCESS:
      return getNCRoamSubscriptionPlansSuccess(state, action as AppAction<NCRoamSubscriptionPlan[] | null>);
    case actionTypes.GET_NC_ROAM_SUBSCRIPTION_PLANS_FAIL:
      return getNCRoamSubscriptionPlansFail(state, action as AppAction<NCRoamSubscriptionPlan[] | null>);

    case actionTypes.GET_NC_ROAM_AGENCY_SUBSCRIPTION_START:
      return getNCRoamAgencySubscriptionStart(state, action as AppAction<NCRoamAgencySubscription | null>);
    case actionTypes.GET_NC_ROAM_AGENCY_SUBSCRIPTION_SUCCESS:
      return getNCRoamAgencySubscriptionSuccess(state, action as AppAction<NCRoamAgencySubscription | null>);
    case actionTypes.GET_NC_ROAM_AGENCY_SUBSCRIPTION_FAIL:
      return getNCRoamAgencySubscriptionFail(state, action as AppAction<NCRoamAgencySubscription | null>);

    case actionTypes.GET_NC_ROAM_USERS_START:
      return getNCRoamUsersStart(state, action as AppAction<NCRoamUser[] | null>);
    case actionTypes.GET_NC_ROAM_USERS_SUCCESS:
      return getNCRoamUsersSuccess(state, action as AppAction<NCRoamUser[] | null>);
    case actionTypes.GET_NC_ROAM_USERS_FAIL:
      return getNCRoamUsersFail(state, action as AppAction<NCRoamUser[] | null>);

    case actionTypes.NC_ROAM_SUBSCRIBE_START:
      return ncRoamSubscribeStart(state, action as AppAction<NCRoamSubscribeResponse | null>);
    case actionTypes.NC_ROAM_SUBSCRIBE_SUCCESS:
      return ncRoamSubscribeSuccess(state, action as AppAction<NCRoamSubscribeResponse | null>);
    case actionTypes.NC_ROAM_SUBSCRIBE_FAIL:
      return ncRoamSubscribeFail(state, action as AppAction<NCRoamSubscribeResponse | null>);

    case actionTypes.NC_ROAM_CANCEL_SUBSCRIPTION_START:
      return ncRoamCancelSubscriptionStart(state, action as AppAction<boolean | null>);
    case actionTypes.NC_ROAM_CANCEL_SUBSCRIPTION_SUCCESS:
      return ncRoamCancelSubscriptionSuccess(state, action as AppAction<boolean | null>);
    case actionTypes.NC_ROAM_CANCEL_SUBSCRIPTION_FAIL:
      return ncRoamCancelSubscriptionFail(state, action as AppAction<boolean | null>);

    case actionTypes.NC_ROAM_UPGRADE_SUBSCRIPTION_START:
      return ncRoamUpgradeSubscriptionStart(state, action as AppAction<boolean | null>);
    case actionTypes.NC_ROAM_UPGRADE_SUBSCRIPTION_SUCCESS:
      return ncRoamUpgradeSubscriptionSuccess(state, action as AppAction<boolean | null>);
    case actionTypes.NC_ROAM_UPGRADE_SUBSCRIPTION_FAIL:
      return ncRoamUpgradeSubscriptionFail(state, action as AppAction<boolean | null>);

    default:
      return state;
  }
};

export default reducer;
